const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

export const toSnakeCase = (str) => {
  return str
    .split(/(?=[A-Z])/)
    .join("_")
    .toLowerCase();
};

export const toUrlEncoded = (obj) => {
  return Object.keys(obj)
    .map((k) => encodeURIComponent(toSnakeCase(k)) + "=" + encodeURIComponent(obj[k]))
    .join("&");
};

export const base64URLEncode = (str) => {
  return str.toString("base64").replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
};

/** Source
 * - base64-arraybuffer library
 * - @see: https://github.com/niklasvh/base64-arraybuffer/blob/master/src/index.ts
 *   License: MIT (@see https://github.com/niklasvh/base64-arraybuffer/blob/master/LICENSE)
 *
 * @param arraybuffer buffer
 * @returns encoded string
 */
const encode = (arraybuffer) => {
  const bytes = new Uint8Array(arraybuffer),
    len = bytes.length;
  let base64 = "";

  for (let i = 0; i < len; i += 3) {
    base64 += chars[bytes[i] >> 2];
    base64 += chars[((bytes[i] & 3) << 4) | (bytes[i + 1] >> 4)];
    base64 += chars[((bytes[i + 1] & 15) << 2) | (bytes[i + 2] >> 6)];
    base64 += chars[bytes[i + 2] & 63];
  }

  if (len % 3 === 2) {
    base64 = base64.substring(0, base64.length - 1) + "=";
  } else if (len % 3 === 1) {
    base64 = base64.substring(0, base64.length - 2) + "==";
  }

  return base64;
};

const getRandomInt = (min, max) => {
  // Create byte array and fill with 1 random number
  const byteArray = new Uint8Array(1);
  window.crypto.getRandomValues(byteArray);

  const range = max - min + 1;
  const max_range = 256;
  if (byteArray[0] >= Math.floor(max_range / range) * range) return getRandomInt(min, max);
  return min + (byteArray[0] % range);
};

/**
 * Creates SHA256 PKCE (Proof Key for Code Exchange) tokens for OAuth Handshake
 *
 * @returns {Promise<{createdAt: Date, codeVerifier: string, codeChallengeMethod: string, codeChallenge: string}>}
 */
export const createPKCECodes = async () => {
  const codeVerifier = `${getRandomInt(64, 256)}`;
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const createdAt = new Date();
  const codePair = {
    codeChallengeMethod: "S256",
    codeVerifier,
    codeChallenge,
    createdAt,
  };
  return codePair;
};

async function generateCodeChallenge(codeVerifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  // Get the appropriate subtle crypto implementation
  const cryptoSubtle = window.crypto.subtle || window.crypto.webkitSubtle;
  if (!cryptoSubtle) {
    throw new Error("Crypto.subtle is not supported in this browser");
  }

  const digest = await cryptoSubtle.digest("SHA-256", data);
  const base64Digest = encode(digest);
  // you can extract this replacing code to a function
  return base64Digest.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

/**
 * If no groups provided, it will just check user is authenticated. If groups are provided, check both if
 * they are authenticated and if they have the appropriate group membership.
 *
 * @param authenticated
 * @param groups
 * @param userInfo
 * @returns {boolean}
 */
export const checkUserPermission = (authenticated = false, groups = [], userInfo = {}) => {
  let isAllowed = authenticated;
  if (authenticated && groups.length) {
    try {
      const memberships = userInfo["cognito:groups"] ?? [];
      isAllowed =
        Array.isArray(memberships) && Array.isArray(groups) ? memberships.some((r) => groups.includes(r)) : false;
    } catch (e) {
      console.error("Error: Check User Permission", e);
      return false;
    }
  }
  return isAllowed;
};

export const jsonToCsv = (data) => {
  // Flatten the JSON objects
  const flattened = data.map(flattenObject);

  // Extract keys for the header row
  const keys = flattened
    .reduce((acc, item) => {
      for (const key of Object.keys(item)) {
        if (acc.indexOf(key) === -1) {
          acc.push(key);
        }
      }
      return acc;
    }, [])
    .sort();

  // Create the header row
  const csvRows = [];
  csvRows.push(keys.join(","));

  // Create data rows
  for (const row of flattened) {
    const values = keys.map((key) => {
      let value = row[key];
      if (typeof value === "string") {
        // Escape double quotes and commas in CSV
        value = '"' + value.replace(/"/g, '""') + '"';
      }
      return value;
    });
    csvRows.push(values.join(","));
  }
  return csvRows.join("\n");
};

export const flattenObject = (data) => {
  const result = {};
  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      if (typeof data[key] === "object" && Object.keys(data[key])) {
        for (const nested of Object.keys(data[key])) {
          console.log(`${key}.${nested}`, data[key][nested]);
          result[`${key}.${nested}`] = data[key][nested];
        }
      } else {
        result[key] = data[key];
      }
    }
  }
  return result;
};
